import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Warning, ArrowRight } from '@phosphor-icons/react';

import type { CoachCard, CoachStarterCard } from '@/api/coach';
import { askAssistant } from '@/lib/askAssistant';

// Shared coach-card renderers. Extracted from CoachDashboard so both the coach
// page and the redesigned home dashboard's action center render the exact same
// rows. Cards don't dead-end: each links into the assistant, which owns the
// approve / navigate affordance.

// askCoach opens the floating AI assistant with a seeded question (the same
// handoff the settings "suggest with AI" buttons and the setup wizard use).
export const askCoach = askAssistant;

export function CardGroup({
  heading,
  cards,
  slug,
  goalTitle,
  risk = false,
}: {
  heading: string;
  cards: CoachCard[];
  slug: string;
  goalTitle: (key: string) => string;
  risk?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground">
        {heading}
      </h2>
      <ul className="flex flex-col gap-3">
        {cards.map((c) => (
          <CoachCardRow
            key={`${heading}-${c.suggestionId}`}
            card={c}
            slug={slug}
            goalTitle={goalTitle}
            risk={risk}
          />
        ))}
      </ul>
    </div>
  );
}

export function CoachCardRow({
  card,
  slug,
  goalTitle,
  risk,
}: {
  card: CoachCard;
  slug: string;
  goalTitle: (key: string) => string;
  risk: boolean;
}) {
  const { t } = useTranslation('common');
  const destructive = risk || card.tone === 'destructive';
  // Same i18n strategy as Assistant.tsx / AssistantDetail.tsx: the sweep writes
  // the card text in English server-side, so translate from the check key and
  // fall back to the server string for rule-origin cards (no check key) and any
  // check whose copy isn't in the locale files yet.
  const title = card.checkKey
    ? t(`assistant.checks.${card.checkKey}.title`, {
        count: card.rowCount,
        defaultValue: card.title,
      })
    : card.title;
  const summary = card.checkKey
    ? t(`assistant.checks.${card.checkKey}.summary`, { defaultValue: card.summary })
    : card.summary;
  return (
    <li
      className={[
        'flex flex-col gap-2 rounded-xl border bg-background p-4 md:flex-row md:items-center md:justify-between md:gap-4',
        destructive ? 'border-destructive/40' : 'border-border',
      ].join(' ')}
    >
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-pill bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
            {goalTitle(card.goalKey ?? '')}
          </span>
          {destructive && <Warning size={16} weight="fill" className="text-destructive" aria-hidden />}
          <h3 className="font-heading text-base font-semibold">{title}</h3>
        </div>
        {summary && <p className="text-sm text-muted-foreground">{summary}</p>}
      </div>
      <Link
        to={`/orgs/${slug}/assistant/${card.suggestionId}`}
        className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-pill border border-border px-4 py-2 text-sm font-semibold transition hover:scale-[1.04] motion-reduce:hover:scale-100"
      >
        {t('coach.dashboard.viewInAssistant')}
        <ArrowRight size={16} weight="bold" aria-hidden />
      </Link>
    </li>
  );
}

// StarterCardRow renders a proactive "get started" step — the coach telling you
// what to DO for a goal it can't measure yet. Its CTA navigates to the setup
// surface (e.g. the Pulse page) rather than the assistant.
export function StarterCardRow({
  starter,
  slug,
  goalTitle,
}: {
  starter: CoachStarterCard;
  slug: string;
  goalTitle: (key: string) => string;
}) {
  const { t } = useTranslation('common');
  return (
    <li className="flex flex-col gap-2 rounded-xl border border-primary/40 bg-background p-4 md:flex-row md:items-center md:justify-between md:gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-pill bg-secondary px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
            {goalTitle(starter.goalKey)}
          </span>
          <h3 className="font-heading text-base font-semibold">
            {t(`coach.dashboard.starter.${starter.key}.title`)}
          </h3>
        </div>
        <p className="text-sm text-muted-foreground">
          {t(`coach.dashboard.starter.${starter.key}.summary`)}
        </p>
      </div>
      <Link
        to={`/orgs/${slug}/${starter.route}`}
        className="inline-flex min-h-11 shrink-0 items-center gap-1 rounded-pill bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:scale-[1.04] motion-reduce:hover:scale-100"
      >
        {t(`coach.dashboard.starter.${starter.key}.cta`)}
        <ArrowRight size={16} weight="bold" aria-hidden />
      </Link>
    </li>
  );
}
